import React, { useState } from "react"
import {
  PlusCircleIcon,
  TrashIcon,
  XMarkIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Checkbox from "aldoo-ra/Checkbox"
import Input from "aldoo-ra/Input"
import Modal from "aldoo-ra/Modal"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { GetSpamList, AddSpamEmail, RemoveSpamEmail } from "./api"
import Value from "aldoo-ra/Value"
import moment from "moment"

export default function SpamBlacklistBrowser() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [processing, setProcessing] = useState(false)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [newEmailValue, setNewEmailValue] = useState("")
  const [reasonValue, setReasonValue] = useState("")
  const [error, setError] = useState("")

  // Get current user profile
  const [userProfile] = Value("user-profile")

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item.email))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection change
  const handleSelectItem = (itemEmail, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemEmail])
    } else {
      setSelectedItems((prev) => prev.filter((email) => email !== itemEmail))
    }
  }

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedItems([])
  }

  // Format the date
  const formatDate = (timestamp) => {
    return timestamp ? moment(timestamp).format("YYYY-MM-DD HH:mm") : "N/A"
  }

  // Format relative time
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return ""
    return moment(timestamp).fromNow()
  }

  // Add email to blacklist
  const handleAddEmailSubmit = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!newEmailValue || !emailRegex.test(newEmailValue)) {
      setError("Please enter a valid email address")
      return
    }

    setProcessing(true)
    setError("")

    try {
      // Get the current user ID
      const userId = userProfile?._id

      const response = await AddSpamEmail(newEmailValue, reasonValue, userId, "blacklist")

      if (response.success) {
        showNotification({
          message: "Email added to blacklist successfully",
          className: "bg-admin_primary text-admin_text_inv",
        })
        setNewEmailValue("")
        setReasonValue("")
        setShowAddDialog(false)

        // Force refresh the table
        setTimeout(() => {
          setRefreshKey((prev) => prev + 1)
        }, 500)
      } else {
        setError(response.error || "Failed to add email to blacklist")
      }
    } catch (error) {
      console.error("Error adding email to blacklist:", error)
      setError("An error occurred. Please try again.")
    } finally {
      setProcessing(false)
    }
  }

  // Remove a single email from blacklist
  const handleRemoveSingleEmail = (email) => {
    showPromptDialog({
      variant: "admin",
      title: "Confirm Remove",
      message: `Are you sure you want to remove ${email} from the blacklist?`,
      confirmText: "Remove",
      confirmVariant: "error_m",
      onConfirm: async () => {
        setProcessing(true)
        try {
          const response = await RemoveSpamEmail(email, "blacklist")

          if (response.success) {
            showNotification({
              message: "Email removed successfully",
              className: "bg-admin_primary text-admin_text_inv",
            })

            // Force a complete refresh
            setTimeout(() => {
              setRefreshKey((prev) => prev + 1)
            }, 500)
          } else {
            showNotification({
              message: response.error || "Failed to remove email",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while removing email",
            className: "bg-admin_error text-white",
          })
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Move a single email to whitelist
  const handleMoveToWhitelist = (email) => {
    showPromptDialog({
      variant: "admin",
      title: "Move to Whitelist",
      message: `Are you sure you want to move ${email} to the whitelist? This will remove it from the blacklist and mark it as trusted.`,
      confirmText: "Move to Whitelist",
      confirmVariant: "primary",
      onConfirm: async () => {
        setProcessing(true)
        try {
          // First remove from blacklist
          const removeResponse = await RemoveSpamEmail(email, "blacklist")
          
          if (removeResponse.success) {
            // Then add to whitelist
            const userId = userProfile?._id
            const addResponse = await AddSpamEmail(email, "Moved from blacklist", userId, "whitelist")
            
            if (addResponse.success) {
              showNotification({
                message: "Email moved to whitelist successfully",
                className: "bg-admin_primary text-admin_text_inv",
              })
              
              // Force a complete refresh
              setTimeout(() => {
                setRefreshKey((prev) => prev + 1)
              }, 500)
            } else {
              showNotification({
                message: addResponse.error || "Failed to add to whitelist",
                className: "bg-admin_error text-white",
              })
            }
          } else {
            showNotification({
              message: removeResponse.error || "Failed to remove from blacklist",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while moving email to whitelist",
            className: "bg-admin_error text-white",
          })
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Move multiple emails to whitelist
  const handleMoveEmailsToWhitelist = () => {
    if (selectedItems.length === 0 || processing) return

    showPromptDialog({
      variant: "admin",
      title: "Move to Whitelist",
      message: `Are you sure you want to move ${selectedItems.length} ${
        selectedItems.length === 1 ? "email" : "emails"
      } to the whitelist? This will remove them from the blacklist and mark them as trusted.`,
      confirmText: "Move to Whitelist",
      confirmVariant: "primary",
      onConfirm: async () => {
        setProcessing(true)
        try {
          let successCount = 0
          let errorCount = 0

          // Process each email sequentially
          for (const email of selectedItems) {
            try {
              // First remove from blacklist
              const removeResponse = await RemoveSpamEmail(email, "blacklist")

              if (removeResponse.success) {
                // Then add to whitelist
                const userId = userProfile?._id
                const addResponse = await AddSpamEmail(email, "Moved from blacklist", userId, "whitelist")

                if (addResponse.success) {
                  successCount++
                } else {
                  errorCount++
                }
              } else {
                errorCount++
              }
            } catch (error) {
              errorCount++
            }
          }

          if (successCount > 0) {
            showNotification({
              message: `${successCount} ${
                successCount === 1 ? "email" : "emails"
              } moved to whitelist successfully${
                errorCount > 0 ? `, ${errorCount} failed` : ""
              }`,
              className: "bg-admin_primary text-admin_text_inv",
            })
          } else if (errorCount > 0) {
            showNotification({
              message: "Failed to move emails to whitelist",
              className: "bg-admin_error text-white",
            })
          }

          // Reset selection and refresh
          setSelectedItems([])
          // Force a complete refresh
          setTimeout(() => {
            setRefreshKey((prev) => prev + 1)
          }, 500)
        } catch (error) {
          showNotification({
            message: "An error occurred while moving emails to whitelist",
            className: "bg-admin_error text-white",
          })
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Remove multiple emails from blacklist
  const handleRemoveEmails = () => {
    if (selectedItems.length === 0 || processing) return

    showPromptDialog({
      variant: "admin",
      title: "Confirm Remove",
      message: `Are you sure you want to remove ${selectedItems.length} ${
        selectedItems.length === 1 ? "email" : "emails"
      } from the blacklist?`,
      confirmText: "Remove",
      confirmVariant: "error_m",
      onConfirm: async () => {
        setProcessing(true)
        try {
          let successCount = 0
          let errorCount = 0

          // Process each email sequentially
          for (const email of selectedItems) {
            try {
              const response = await RemoveSpamEmail(email, "blacklist")

              if (response.success) {
                successCount++
              } else {
                errorCount++
              }
            } catch (error) {
              errorCount++
            }
          }

          if (successCount > 0) {
            showNotification({
              message: `${successCount} ${
                successCount === 1 ? "email" : "emails"
              } removed successfully${
                errorCount > 0 ? `, ${errorCount} failed` : ""
              }`,
              className: "bg-admin_primary text-admin_text_inv",
            })
          } else if (errorCount > 0) {
            showNotification({
              message: "Failed to remove emails",
              className: "bg-admin_error text-white",
            })
          }

          // Reset selection and refresh
          setSelectedItems([])
          // Force a complete refresh
          setTimeout(() => {
            setRefreshKey((prev) => prev + 1)
          }, 500)
        } catch (error) {
          showNotification({
            message: "An error occurred while removing emails",
            className: "bg-admin_error text-white",
          })
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Style overrides for the DataTable
  const styleOverrides = {
    container: "relative w-full flex flex-col h-[75vh] justify-start bg-admin_paper rounded-3xl p-6 pb-4",
    table: "w-full table-fixed",
    header: "w-full flex justify-between items-end gap-2 mt-10 mb-4",
    searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
    row: "h-[60px] text-admin_text text-sm xl:text-base font-normal",
    getRowStyle: (index, item) => {
      // Check if this item is selected
      if (item && selectedItems.includes(item.email)) {
        return "bg-admin_canvas" // Selected row style
      }
    },
  }

  // Column definitions with selection checkbox
  const columns = [
    {
      id: "select",
      headerClassName: "w-8",
      header: (value, items) => (
        <Checkbox
          variant="admin_compact"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin_compact"
          className="mt-0.5"
          isOn={selectedItems.includes(item.email)}
          onChange={(isSelected) => handleSelectItem(item.email, isSelected)}
        />
      ),
    },
    {
      id: "email",
      title: "Email",
      headerClassName: "w-[20%] whitespace-nowrap",
      render: (_, item) => (
        <Tooltip 
          display="block" 
          content={item.email}
        >
          <div className="flex items-center">
            <EnvelopeIcon className="w-4 xl:w-5 h-4 xl:h-5 flex-shrink-0 text-admin_text_secondary mr-2" />

            <span className="block font-medium truncate">
              {item.email}
            </span>
          </div>
        </Tooltip>
      ),
    },
    {
      id: "reason",
      title: "Reason",
      headerClassName: "w-[16%] whitespace-nowrap",
      render: (_, item) => (
        <Tooltip 
          display="block" 
          content={item.reason || "No reason specified"}
        >
          <span className="block truncate">
            {item.reason || "No reason specified"}
          </span>
        </Tooltip>
      ),
    },
    {
      id: "hitCount",
      title: "Hit Count",
      headerClassName: "w-[10%] whitespace-nowrap text-center",
      render: (_, item) => (
        <span className="block text-center">
          {item.hitCount || 0}
        </span>
      ),
    },
    {
      id: "addedAt",
      title: "Added",
      headerClassName: "w-[15%] whitespace-nowrap",
      render: (_, item) => (
        <div className="flex flex-col mt-2.5">
          <span className="flex-nowrap">
            {formatDate(item.addedAt)}
          </span>

          <span className="text-xs text-admin_text_secondary leading-none">
            {getRelativeTime(item.addedAt)}
          </span>
        </div>
      ),
    },
    {
      id: "lastHit",
      title: "Last Hit",
      headerClassName: "w-[15%] whitespace-nowrap",
      render: (_, item) => (
        <div>
          {item.lastHit ? (
            <div className="flex flex-col mt-2.5">
              <span className="flex-nowrap">
                {formatDate(item.lastHit)}
              </span>

              <span className="text-xs text-admin_text_secondary leading-none">
                {getRelativeTime(item.lastHit)}
              </span>
            </div>
          ) : (
            <span className="text-admin_text_secondary">
              Never
            </span>
          )}
        </div>
      ),
    },
    {
      id: "addedBy",
      title: "Added By",
      headerClassName: "w-[10%] whitespace-nowrap",
      render: (_, item) => (
        <Tooltip
          display="block"
          content={item.addedBy ? (item.addedBy.name || item.addedBy.email || "Unknown") : "Unknown"}
        >
        <div>
          {item.addedBy ? (
            <span className="block truncate">
              {item.addedBy.name || item.addedBy.email || "Unknown"}
            </span>
          ) : (
            <span>
              System
            </span>
          )}
        </div>
        </Tooltip>
      ),
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[10%] whitespace-nowrap",
      render: (_, item) => (
        <div className="flex gap-2">
          <Tooltip 
            display="block" 
            content="Move to whitelist"
          >
            <Button
              variant="admin_icon"
              className="p-1.5 text-admin_text_secondary transition-colors"
              onClick={() => handleMoveToWhitelist(item.email)}
            >
              <CheckCircleIcon className="w-5 h-5" />
            </Button>
          </Tooltip>

          <Tooltip 
            display="block" 
            content="Remove from blacklist"
          >
            <Button
              variant="admin_icon"
              className="p-1.5 text-admin_text_secondary hover:!text-admin_error transition-colors"
              onClick={() => handleRemoveSingleEmail(item.email)}
            >
              <TrashIcon className="w-5 h-5" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ]

  // Custom API method that filters for blacklist entries (excludes whitelist)
  const getBlacklistEntries = async (options = {}) => {
    return await GetSpamList({ ...options, entryType: "blacklist" })
  }

  {/* Table footer content - action buttons */}
  const tableFooter = selectedItems.length > 0 && (
    <div className="flex justify-end items-center mt-4 p-3 bg-admin_paper rounded">
      <Typography variant="admin_body1" className="mr-4">
        {selectedItems.length}{" "}
        {selectedItems.length === 1 ? "email" : "emails"} selected
      </Typography>

      <Button
        variant="admin_secondary"
        className="mr-3"
        onClick={handleClearSelection}
        disabled={processing}
      >
        <div className="flex items-center">
          <XMarkIcon className="w-5 h-5 mr-2" />
          Deselect
        </div>
      </Button>

      <Button
        variant="admin_secondary"
        className="mr-3"
        onClick={handleMoveEmailsToWhitelist}
        disabled={processing}
      >
        <div className="flex items-center">
          <CheckCircleIcon className="w-5 h-5 mr-2" />
          Move to Whitelist
        </div>
      </Button>

      <Button
        variant="admin_error"
        onClick={handleRemoveEmails}
        disabled={processing}
      >
        <div className="flex items-center">
          <TrashIcon className="w-5 h-5 mr-2" />
          Remove
        </div>
      </Button>
    </div>
  )

  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
        >
          Spam Blacklist
        </Typography>

        <Button
          variant="admin_primary"
          className="whitespace-nowrap flex items-center"
          onClick={() => setShowAddDialog(true)}
        >
          <PlusCircleIcon className="w-5 h-5 mr-2" /> Add
        </Button>
      </div>

      {/* Description */}
      <div className="mb-6 bg-admin_paper p-4 rounded-lg border-l-4 border-admin_error">
        <Typography variant="admin_body1" className="text-admin_text_secondary my-0">
          <strong>Spam Blacklist:</strong> Email addresses in this list are automatically marked as spam and blocked. 
          These emails will be rejected by the spam protection system. You can move entries to the whitelist 
          to mark them as trusted, or remove them entirely to allow normal spam filtering.
        </Typography>
      </div>

      {/* Main data table */}
      <DataTable
        variant="admin_browser"
        key={`spam-blacklist-${refreshKey}`}
        apiMethod={getBlacklistEntries}
        searchField="email reason" // This is correct - tells DataTable which fields to search
        sort={{ addedAt: -1 }} // Default sort
        localUserPreferencesKey="admin.spamProtection.blacklist"
        renders={columns}
        refreshKey={refreshKey}
        preferredHeight="60vh"
        itemHeight="h-16"
        tableFooter={tableFooter}
        noData={
          <Typography variant="admin_body1">
            No blacklisted emails found
          </Typography>
        }
        styleOverrides={styleOverrides}
      />

      {/* Add Email Dialog */}
      <Modal
        isOpen={showAddDialog}
        onClose={() => {
          setShowAddDialog(false)
          setNewEmailValue("")
          setReasonValue("")
          setError("")
        }}
        title="Add Email to Blacklist"
        variant="admin_centered"
      >
        <div className="pt-6 space-y-6">
          {error && (
            <div className="p-3 mb-4 bg-admin_error/10 border border-admin_error text-admin_error rounded-md flex items-start">
              <ExclamationCircleIcon className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-4">
            <Input
              variant="admin"
              label="Email Address"
              value={newEmailValue}
              onChange={(e) => setNewEmailValue(e.target.value)}
              placeholder="Enter email to blacklist"
              required
              styleOverrides={{
                label: "text-base font-bold text-admin_text mb-2",
              }}
            />

            <Input
              label="Reason (optional)"
              variant="admin"
              value={reasonValue}
              onChange={(e) => setReasonValue(e.target.value)}
              placeholder="Enter reason for blacklisting"
              multiline
              styleOverrides={{
                label: "text-base font-bold text-admin_text mb-2",
              }}
            />
          </div>

          <div className="flex justify-end gap-4 mt-8">
            <Button
              variant="admin_secondary"
              onClick={() => {
                setShowAddDialog(false)
                setNewEmailValue("")
                setReasonValue("")
                setError("")
              }}
              disabled={processing}
            >
              Cancel
            </Button>

            <Button
              variant="admin_primary"
              onClick={handleAddEmailSubmit}
              disabled={processing || !newEmailValue.trim()}
            >
              {processing ? "Adding..." : "Add to Blacklist"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
